// src/config/packageLayoutConfig.js
//
// FRONTEND PACKAGE LAYOUT CONFIG
// ==============================
//
// This file configures the HTML/CSS PACKAGE preview layer.
//
// WHY THIS FILE EXISTS
// --------------------
// You decided to physically split voucher and package template families.
// That decision should also be reflected on the frontend configuration layer.
//
// Therefore:
//
//   - voucherLayoutConfig.js  -> voucher-only config
//   - packageLayoutConfig.js  -> package-only config
//
// This file is the package-side equivalent of voucherLayoutConfig.js.
// It intentionally mirrors a very similar API shape so mixed UI pages
// (like VoucherPage.js, which actually handles both vouchers and packages)
// can switch between the two configs cleanly.
//
// WHERE IT IS USED
// ----------------
// This config is intended for frontend places that need to operate in
// package mode, for example:
//
//   - mixed backoffice pages that can edit both vouchers and packages
//   - future package-specific preview components
//   - future package-specific admin selectors for motif/theme/layout
//
// IMPORTANT
// ---------
// The backend still stores package rows in the shared `vouchers` table,
// but from the document/layout perspective packages are now treated as
// a separate business family.
//
// That means this file should be understood as:
//
//   package business-family preview config
//
// and NOT as a generic "all document types" config.
//
// RELATIONSHIP TO BACKEND
// -----------------------
// This frontend config should conceptually mirror:
//
//   backend/pdf/voucherThemeConfig.js
//
// especially for:
//
//   - business mode separation
//   - theme keys
//   - template-version catalog
//   - templateVersion -> theme mapping
//
// The exact HTML preview styling may differ slightly from backend PDF output,
// but the business meaning of the keys should stay aligned.
//
// IMPORTANT COMPATIBILITY NOTE
// ----------------------------
// We intentionally keep the exported object shape similar to
// voucherLayoutConfig.js.
//
// For example, we still expose:
//
//   packageLayoutConfig.voucherLabel
//
// even though the business meaning is "package label".
//
// Why keep that slightly odd property name?
// -----------------------------------------
// Because it makes migration easier for preview components that may already
// expect a shape similar to voucherLayoutConfig.
// In other words, the PROPERTY contract stays familiar, while the CONTENT
// becomes package-specific.
//

// ---------------------------------------------------------------------------
// THEME REGISTRY (FRONTEND VIEW – PACKAGE FAMILY)
// ---------------------------------------------------------------------------
//
// Canonical theme keys remain shared across voucher/package preview for now:
//
//   - "default"
//   - "christmas"
//   - "valentine"
//
// This is acceptable because:
//   - package template versions are already separate from voucher versions
//   - package themes may still reuse the same canonical visual token keys
//
// Later you can introduce package-only themes if needed.
//
export const PACKAGE_THEMES = {
  default: {
    key: "default",
    label: "Klasyczny",
  },

  christmas: {
    key: "christmas",
    label: "Świąteczny",
  },

  valentine: {
    key: "valentine",
    label: "Walentynkowy",
  },

  // NOTE:
  //  - "warm" is a legacy key kept for backward compatibility.
  //  - We do not treat it as a preferred modern package theme key.
  //  - `normalizeTheme("warm")` returns "valentine".
  warm_legacy: {
    key: "warm",
    label: "Ciepły (legacy)",
  },
};

// Canonical theme keys list (only real, currently supported themes).
export const SUPPORTED_THEME_KEYS = ["default", "christmas", "valentine"];

// ---------------------------------------------------------------------------
// TEMPLATE VERSION -> THEME MAPPING (PACKAGE FAMILY)
// ---------------------------------------------------------------------------
//
// IMPORTANT
// ---------
// These are PACKAGE motif/version keys, not voucher keys.
//
// Backend-aligned package template versions:
//
//   - "package_classic"
//   - "package_premium"
//   - "package_gold"
//
// Current mapping mirrors backend/pdf/voucherThemeConfig.js:
//
//   package_classic -> default
//   package_premium -> valentine
//   package_gold    -> christmas
//
// This mapping is intentionally simple and stable.
// Later, if backend introduces package-only theme keys, update this map.
//
export const templateVersionToThemeMap = {
  package_classic: "default",
  package_premium: "valentine",
  package_gold: "christmas",
};

// ---------------------------------------------------------------------------
// CORE LAYOUT CONFIG
// ---------------------------------------------------------------------------
//
// NOTE:
//   - The object shape intentionally stays very close to voucherLayoutConfig.js
//   - This makes migration easier for mixed pages/components
//
export const packageLayoutConfig = {
  // Displayed salon name when caller does not override it.
  salonName: "HB Health & Beauty",

  // Optional location line for symmetry with voucher config.
  locationLine: "Konin",

  // IMPORTANT:
  // Property name intentionally kept as `voucherLabel` for drop-in compatibility
  // with existing preview patterns. The TEXT itself is package-specific.
  voucherLabel: "Pakiet podarunkowy",

  // Generic package validity text used by preview when no explicit label exists.
  //
  // IMPORTANT:
  // This is only a frontend preview fallback. Backend PDFs may still display
  // a concrete business expiration date when available.
  validityText: "Pakiet ważny przez 12 miesięcy od daty zakupu.",

  // Optional footer note kept for symmetry and future preview reuse.
  footerNote:
    "Szczegółowe zasady wykorzystania pakietu znajdziesz w Regulaminie salonu.",

  // Theme registry and defaults.
  themes: PACKAGE_THEMES,
  defaultThemeKey: "default",

  // Expose package template-version mapping directly on the config object too.
  templateVersionToThemeMap,

  // -------------------------------------------------------------------------
  // THEME COLOR TOKENS (HTML PREVIEW ONLY)
  // -------------------------------------------------------------------------
  //
  // These tokens are for HTML/CSS preview usage on the frontend.
  // They do NOT directly control backend PDF colors.
  //
  // For now they intentionally stay close to voucher preview colors because:
  //   - your first goal is architectural split
  //   - not immediate visual divergence
  //
  colors: {
    default: {
      background: "#ffffff",
      accent: "#111111",
      text: "#111111",
      subtleText: "#555555",
      border: "#e5e5e5",
      qrBackground: "#f5f5f5",
    },

    valentine: {
      background: "#fff8f0",
      accent: "#c77b4a",
      text: "#3b2e25",
      subtleText: "#7b5c47",
      border: "#f0d2b8",
      qrBackground: "#fff1e0",
    },

    // Legacy alias palette.
    warm: {
      background: "#fff8f0",
      accent: "#c77b4a",
      text: "#3b2e25",
      subtleText: "#7b5c47",
      border: "#f0d2b8",
      qrBackground: "#fff1e0",
    },

    christmas: {
      background: "#ffffff",
      accent: "#0b6623",
      text: "#111111",
      subtleText: "#555555",
      border: "#e0e0e0",
      qrBackground: "#f6fff6",
    },
  },

  // Spacing tokens kept for parity with voucher config.
  spacing: {
    pagePadding: 40,
    sectionGap: 16,
    headerGap: 24,
  },
};

// ---------------------------------------------------------------------------
// normalizeTheme(theme)
// ---------------------------------------------------------------------------
//
// Normalizes any input string into one of the canonical supported package
// theme keys:
//
//   - "default"
//   - "christmas"
//   - "valentine"
//
// Backward compatibility:
//   - "warm" -> "valentine"
//   - unknown / empty -> "default"
//
// IMPORTANT
// ---------
// Even though this is package config, canonical theme keys are currently
// shared with voucher config. That is acceptable.
// The separation here is about config ownership and business family,
// not about forcing unique theme names immediately.
//
export function normalizeTheme(theme) {
  if (!theme || typeof theme !== "string") {
    return packageLayoutConfig.defaultThemeKey;
  }

  const t = theme.trim().toLowerCase();

  // Legacy alias -> canonical valentine theme.
  if (t === "warm") {
    return "valentine";
  }

  if (SUPPORTED_THEME_KEYS.includes(t)) {
    return t;
  }

  return packageLayoutConfig.defaultThemeKey;
}

/**
 * getThemePreset(themeKey)
 *
 * Convenience helper for package-oriented UI code:
 *   - accepts any theme-like string
 *   - normalizes it
 *   - returns corresponding metadata object
 */
export function getThemePreset(themeKey) {
  const normalizedKey = normalizeTheme(themeKey);

  if (normalizedKey === "valentine") {
    return PACKAGE_THEMES.valentine;
  }

  return PACKAGE_THEMES[normalizedKey] || PACKAGE_THEMES.default;
}

/**
 * getThemeForTemplateVersion(version)
 *
 * High-level helper for package motif -> theme derivation.
 *
 * Example package mappings:
 *   - package_classic -> default
 *   - package_premium -> valentine
 *   - package_gold    -> christmas
 *
 * If unknown value is provided, we fall back conservatively.
 */
export function getThemeForTemplateVersion(version) {
  if (!version) {
    return packageLayoutConfig.defaultThemeKey;
  }

  const v = String(version).trim().toLowerCase();
  const mapped = templateVersionToThemeMap[v];

  if (mapped) {
    return mapped;
  }

  // Fallback: if caller accidentally passes a raw theme key instead of a
  // package templateVersion, try to normalize it anyway.
  return normalizeTheme(v);
}